import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Truck, FlaskConical, Wrench, Factory, Building2, Search, Banknote, ClipboardCheck,
  Thermometer, Droplets, MapPin, User as UserIcon, Calendar, CheckCircle2, AlertTriangle, XCircle,
  ShoppingCart, CreditCard, Wallet, MessageCircle, Receipt, RotateCcw, ArrowDownLeft, Package, Printer, Milk,
} from "lucide-react";
import { toast } from "sonner";
import { QualityDot } from "@/components/Quality";

const fmt = (n, d = 2) => (n ?? 0).toLocaleString("es-MX", { minimumFractionDigits: d, maximumFractionDigits: d });
const money = (n) => n != null ? `$${(n).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—";
const dt = (s) => s ? new Date(s).toLocaleString("es-MX") : "—";

const COMPANY = { name: "LácteoPro", address: "Planta procesadora de lácteos", rfc: "XAXX010101000" };

const PrintBrand = () => (
  <div className="flex items-center gap-3">
    <div className="w-10 h-10 rounded-md bg-[#1E3A8A] flex items-center justify-center flex-shrink-0">
      <Milk className="w-6 h-6 text-white" strokeWidth={1.75} />
    </div>
    <div>
      <div className="font-outfit font-bold text-lg text-stone-900 leading-tight">{COMPANY.name}</div>
      <div className="text-[10px] uppercase tracking-widest text-stone-500 font-outfit">{COMPANY.address} · RFC {COMPANY.rfc}</div>
    </div>
  </div>
);

// Isolates a single <Section id="…"> for printing/PDF, restores everything after
const printSection = (id, docTitle) => {
  document.body.classList.add("print-section-mode");
  const style = document.createElement("style");
  style.setAttribute("data-print-isolate", "true");
  style.innerHTML = `@media print { [data-print-section]:not([data-print-section="${id}"]) { display: none !important; } }`;
  document.head.appendChild(style);
  const prevTitle = document.title;
  if (docTitle) document.title = docTitle;
  const cleanup = () => {
    document.body.classList.remove("print-section-mode");
    style.remove();
    document.title = prevTitle;
    window.removeEventListener("afterprint", cleanup);
  };
  window.addEventListener("afterprint", cleanup);
  setTimeout(cleanup, 60000); // safety net if afterprint never fires (e.g. dialog cancelled on some browsers)
  setTimeout(() => window.print(), 50);
};

export default function Trazabilidad() {
  const [params, setParams] = useSearchParams();
  const [lote, setLote] = useState(params.get("lote") || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const search = async (loteVal) => {
    const q = (loteVal ?? lote).trim();
    if (!q) return;
    setLoading(true);
    setData(null);
    try {
      const { data } = await api.get(`/traceability/${encodeURIComponent(q)}`);
      setData(data);
      setLote(q);
      setParams({ lote: q });
    } catch { toast.error("Lote no encontrado"); }
    finally { setLoading(false); }
  };

  // Auto-search if ?lote= present
  useEffect(() => {
    const initial = params.get("lote");
    if (initial) {
      setLote(initial);
      search(initial);
    }
    // eslint-disable-next-line
  }, []);

  const folioActual = data?.lote_origen || data?.shipment?.folio || lote;

  return (
    <div className="max-w-screen-xl mx-auto p-6 lg:p-8 space-y-6" data-testid="trazabilidad-page">
      <style>{`@media print { body.print-section-mode .global-print-header { display: none !important; } }`}</style>

      <div className="print:hidden">
        <h1 className="font-outfit font-bold text-3xl tracking-tight text-stone-900">Trazabilidad 360°</h1>
        <p className="text-sm text-stone-500 mt-1">Expediente completo del lote: proveedor, recepción, laboratorio, correcciones, producción y análisis de compra.</p>
      </div>

      <Card className="p-5 border-stone-200 bg-white shadow-sm print:hidden">
        <form onSubmit={(e) => { e.preventDefault(); search(); }} className="flex gap-2">
          <Input value={lote} onChange={(e) => setLote(e.target.value)} placeholder="MP-…  ·  CRE-…  ·  CUA-…  ·  CREC-…  ·  CUAC-…  ·  VTA-…  ·  EMB-…" data-testid="trace-input" />
          <Button type="submit" disabled={loading} data-testid="trace-search-btn" className="bg-[#1E3A8A] hover:bg-[#1E40AF]">
            <Search className="w-4 h-4 mr-1.5" /> Rastrear
          </Button>
        </form>
      </Card>

      {data && (
        <>
          <div className="global-print-header hidden print:block border-b-2 border-[#1E3A8A] pb-4 mb-2">
            <div className="flex items-center justify-between">
              <PrintBrand />
              <div className="text-right">
                <div className="font-outfit font-bold text-sm uppercase tracking-wide text-[#1E3A8A]">Expediente de Trazabilidad</div>
                <div className="text-xs text-stone-500 mt-0.5">{new Date().toLocaleString("es-MX")}</div>
              </div>
            </div>
            <div className="mt-3 text-2xl font-outfit font-bold text-stone-900">Lote {folioActual}</div>
          </div>

          <div className="flex justify-end print:hidden">
            <Button variant="outline" onClick={() => {
              const prevTitle = document.title;
              document.title = `Trazabilidad - ${folioActual} - LácteoPro`;
              const cleanup = () => { document.title = prevTitle; window.removeEventListener("afterprint", cleanup); };
              window.addEventListener("afterprint", cleanup);
              setTimeout(cleanup, 60000);
              window.print();
            }} data-testid="print-all-btn">
              <Printer className="w-4 h-4 mr-1.5" /> Imprimir todo (PDF)
            </Button>
          </div>
        </>
      )}

      {data && data.kind === "shipment" ? <ShipmentDossier data={data} onSelect={search} />
        : data && data.kind === "cream_curd_reception" ? <CreamCurdDossier data={data} onSelect={search} />
        : data && <FullDossier data={data} onSelect={search} />}
    </div>
  );
}

const FullDossier = ({ data, onSelect }) => {
  const r = data.reception;
  const a = r?.purchase_analysis;
  return (
    <div className="space-y-6" data-testid="trace-timeline">
      {/* HEADER */}
      <Card className="p-6 border-stone-200 dark:border-stone-800 bg-gradient-to-br from-stone-50 to-white dark:from-stone-900/40 dark:to-stone-950 shadow-sm">
        {data.linked_via_sale && (
          <div className="mb-4 pb-4 border-b border-stone-200">
            <div className="flex items-start gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-[#1E3A8A] text-white text-[10px] uppercase tracking-wider font-outfit font-semibold">
                    <ShoppingCart className="w-3 h-3" /> Trazabilidad vía venta
                  </span>
                  <span className="text-sm font-outfit font-semibold text-[#1E3A8A]">{data.linked_via_sale.folio}</span>
                  <span className="text-stone-400">·</span>
                  <span className="text-sm font-medium text-stone-800">{data.linked_via_sale.customer_name}</span>
                  <span className="text-stone-400">·</span>
                  <span className="text-sm tabular-nums text-stone-700">{money(data.linked_via_sale.total)}</span>
                  {(data.linked_via_sale.balance || 0) > 0 && (
                    <span className="inline-block px-2 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] uppercase tracking-wider font-outfit font-semibold">Saldo {money(data.linked_via_sale.balance)}</span>
                  )}
                </div>
                {data.linked_via_sale.reception_folios?.length > 1 && (
                  <div className="mt-2.5">
                    <div className="text-[10px] uppercase tracking-wider font-outfit font-semibold text-stone-500 mb-1.5">
                      Esta venta involucra {data.linked_via_sale.reception_folios.length} lotes — abre otro:
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {data.linked_via_sale.reception_folios.map((f) => (
                        <button
                          key={f}
                          disabled={f === data.lote_origen}
                          onClick={() => f !== data.lote_origen && onSelect && onSelect(f)}
                          className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded border font-mono transition-colors ${
                            f === data.lote_origen
                              ? "bg-[#1E3A8A] text-white border-[#1E3A8A] cursor-default"
                              : "bg-white text-[#1E3A8A] border-stone-300 hover:bg-[#1E3A8A]/5 cursor-pointer"
                          }`}
                          data-testid={`related-lote-${f}`}
                        >
                          {f} {f === data.lote_origen && "· mostrando"}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs font-outfit font-semibold uppercase tracking-[0.15em] text-stone-500">Lote de origen</div>
            <div className="text-3xl font-outfit font-bold text-stone-900 mt-1">{data.lote_origen}</div>
            <div className="text-sm text-stone-600 mt-2">{r.supplier_name} · Recibido el {r.reception_date || dt(r.created_at).slice(0,10)} por {r.received_by}</div>
          </div>
          {r.quality?.overall && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-white border border-stone-200">
              <QualityDot status={r.quality.overall.status} />
              <div>
                <div className="text-[10px] uppercase tracking-wider font-outfit font-semibold text-stone-500">Calidad recepción</div>
                <div className="text-sm font-medium text-stone-900">{r.quality.overall.label}</div>
              </div>
            </div>
          )}
        </div>

        {a && (
          <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3 pt-5 border-t border-stone-200">
            <Stat label="Decisión IA" value={a.decision} accent={a.decision === "COMPRAR" ? "emerald" : a.decision === "NEGOCIAR PRECIO" ? "amber" : "red"} />
            <Stat label="Margen estimado" value={`${a.margin_pct}%`} />
            <Stat label="Ingreso esperado" value={money(a.total_income)} />
            <Stat label="Utilidad ajustada" value={money(a.adjusted_profit)} />
          </div>
        )}
      </Card>

      {/* PROVEEDOR */}
      <Section icon={Building2} title="Proveedor" tone="stone" id="proveedor">
        <Grid>
          <KV k="Razón social" v={data.supplier?.name} />
          <KV k="Contacto" v={data.supplier?.contact} />
          <KV k="Teléfono" v={data.supplier?.phone} />
          <KV k="Notas" v={data.supplier?.notes} />
        </Grid>
      </Section>

      {/* RECEPCIÓN */}
      <Section icon={Truck} title={`Recepción · ${r.folio}`} tone="navy" id="recepcion">
        <Grid cols={3}>
          <KV k="Fecha de recepción" v={r.reception_date || dt(r.created_at)?.slice(0,10)} />
          <KV k="Hora de captura" v={dt(r.created_at)} />
          <KV k="Recibido por" v={r.received_by} />
          <KV k="Transportista" v={r.transporter_name || "—"} />
          <KV k="Litros" v={r.liters ? `${fmt(r.liters, 2)} L` : "—"} highlight />
          <KV k="Peso neto" v={r.weight_kg ? `${fmt(r.weight_kg, 2)} kg` : "—"} />
          <KV k="Densidad" v={r.density ? `${r.density} g/mL` : "—"} />
          <KV k="Temperatura" v={r.temperature_c != null ? `${r.temperature_c} °C` : "—"} />
          <KV k="Grasa" v={r.fat_pct != null ? `${r.fat_pct}%` : "—"} />
          <KV k="Puntos de grasa" v={r.fat_points != null ? r.fat_points.toLocaleString("es-MX", { minimumFractionDigits: 3, maximumFractionDigits: 3 }) : (r.liters && r.fat_pct ? (r.liters * r.fat_pct / 100).toLocaleString("es-MX", { minimumFractionDigits: 3, maximumFractionDigits: 3 }) : "—")} highlight />
          <KV k="pH" v={r.ph ?? "—"} />
          <KV k="Acidez" v={r.acidity != null ? `${r.acidity} °D` : "—"} />
          <KV k="Observaciones" v={r.observations || "—"} />
        </Grid>

        {/* Quality breakdown */}
        {r.quality?.fields && (
          <div className="mt-4 pt-4 border-t border-stone-200">
            <div className="text-[10px] uppercase tracking-wider font-outfit font-semibold text-stone-500 mb-2">Semáforo de calidad</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(r.quality.fields).map(([k, v]) => (
                <QualityPill key={k} label={k === "fat_pct" ? "Grasa" : k === "temperature_c" ? "Temperatura" : k === "ph" ? "pH" : "Acidez"} info={v} />
              ))}
            </div>
          </div>
        )}

        {/* Costos */}
        {!r.costs_hidden && (
          <div className="mt-4 pt-4 border-t border-stone-200">
            <div className="text-[10px] uppercase tracking-wider font-outfit font-semibold text-stone-500 mb-2">Costos de compra</div>
            <Grid cols={4}>
              <KV k="Precio compra" v={r.purchase_price_per_l ? `$${r.purchase_price_per_l}/L` : "—"} />
              <KV k="Costo de la leche" v={money(r.purchase_cost)} />
              <KV k="Transporte" v={money(r.transport_cost)} />
              <KV k="Descarga" v={money(r.unload_cost)} />
              <KV k="Costo inicial del lote" v={money(r.initial_lot_cost)} highlight />
              <KV k="Forma de pago" v={r.payment_method === "credito" ? `Crédito ${r.credit_days || 0}d` : "Contado"} />
            </Grid>
            {r.payment_method === "credito" && (
              <div className="mt-3 border border-emerald-200 bg-emerald-50 rounded-md p-3">
                <div className="text-[10px] uppercase tracking-wider font-outfit font-semibold text-emerald-700 mb-1">Compra de leche ligada en Cuentas por Pagar</div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                  <div><span className="text-stone-500">Folio:</span> <Link to={`/cuentas-por-pagar`} className="font-mono text-[#1E3A8A] hover:underline">{r.folio}</Link></div>
                  <div><span className="text-stone-500">Total:</span> <span className="tabular-nums">{money(r.purchase_cost)}</span></div>
                  <div><span className="text-stone-500">Saldo:</span> <span className={`tabular-nums font-semibold ${(r.balance || 0) > 0 ? "text-amber-700" : "text-emerald-700"}`}>{money(r.balance)}</span></div>
                  <div><span className="text-stone-500">Vencimiento:</span> <span className="tabular-nums">{r.due_date || "—"}</span></div>
                </div>
              </div>
            )}
          </div>
        )}
      </Section>

      {/* LABORATORIO ahora se muestra agrupado por tipo (ver arriba) */}

      {/* CORRECCIONES */}
      <Section icon={Wrench} title={`Correcciones aplicadas al lote ${r.folio} (${data.corrections.length})`} tone="orange" id="correcciones">
        <p className="text-xs text-stone-500 mb-4 -mt-2">→ Estas correcciones afectan directamente al lote de materia prima <b className="text-stone-700">{r.folio}</b> y su costo.</p>
        {data.corrections.length === 0 ? (
          <Empty>Sin correcciones aplicadas.</Empty>
        ) : data.corrections.map((c) => (
          <div key={c.id} className="border border-stone-200 rounded-lg p-4 mb-3 last:mb-0 bg-stone-50/40">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs text-stone-500">{dt(c.created_at)} · {c.responsible}</div>
              <div className="text-sm font-outfit font-semibold text-[#1E3A8A] tabular-nums">{money(c.total_cost)}</div>
            </div>
            {c.items && c.items.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-white border-b border-stone-200">
                    <tr>{["Insumo", "Kg", "Precio $/kg", "Costo"].map((h) => <th key={h} className="px-3 py-2 text-left text-[10px] uppercase tracking-wider font-outfit font-semibold text-stone-500">{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {c.items.map((it, j) => (
                      <tr key={j} className="border-b border-stone-100 last:border-0">
                        <td className="px-3 py-2 font-medium text-stone-900">{it.name}</td>
                        <td className="px-3 py-2 tabular-nums">{it.kg} kg</td>
                        <td className="px-3 py-2 tabular-nums text-stone-700">${it.price_per_kg}</td>
                        <td className="px-3 py-2 tabular-nums font-medium">{money(it.cost)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <Grid cols={4}>
                <KV k="Base" v={`${c.base_kg ?? 0} kg`} />
                <KV k="Costo base" v={money(c.base_cost)} />
                <KV k="Caseína" v={`${c.caseina_kg ?? 0} kg`} />
                <KV k="Costo caseína" v={money(c.caseina_cost)} />
              </Grid>
            )}
            {c.final_result && <div className="mt-3 text-xs text-stone-600"><b>Resultado:</b> {c.final_result}</div>}
          </div>
        ))}
      </Section>

      {/* LABORATORIO agrupado por tipo */}
      {(() => {
        const rawMilkSamples = data.lab_samples.filter((s) => (s.sample_type || "raw_milk") === "raw_milk");
        const creamSamples = data.lab_samples.filter((s) => s.sample_type === "cream");
        const curdSamples = data.lab_samples.filter((s) => s.sample_type === "curd");
        return (
          <>
            <LabGroup title={`Laboratorio · Leche cruda (${rawMilkSamples.length})`} description={<>→ Pruebas hechas al lote de materia prima <b className="text-stone-700">{r.folio}</b> antes de procesar.</>} samples={rawMilkSamples} tone="amber" showFull id="lab-leche-cruda" />
            <LabGroup title={`Laboratorio · Crema (${creamSamples.length})`} description={<>→ Pruebas hechas al lote de crema <b className="text-stone-700">{data.productions[0]?.cream_folio || "CRE-..."}</b> generado de este MP.</>} samples={creamSamples} tone="amber" showFull id="lab-crema" />
            <LabGroup title={`Laboratorio · Cuajada (${curdSamples.length})`} description={<>→ Pruebas hechas al lote de cuajada <b className="text-stone-700">{data.productions[0]?.curd_folio || "CUA-..."}</b> generado de este MP.</>} samples={curdSamples} tone="amber" showFull={false} id="lab-cuajada" />
          </>
        );
      })()}

      {/* PRODUCCIÓN */}
      <Section icon={Factory} title={`Producción (${data.productions.length})`} tone="emerald" id="produccion">
        {data.productions.length === 0 ? (
          <Empty>Aún no se ha procesado este lote.</Empty>
        ) : data.productions.map((p) => (
          <div key={p.id} className="border border-stone-200 rounded-lg p-4 mb-3 last:mb-0 bg-stone-50/40">
            <div className="text-xs text-stone-500 mb-2">{dt(p.created_at)} · {p.operator} · Turno {p.shift}</div>
            <Grid cols={4}>
              <KV k="Lote Crema" v={p.cream_folio} highlight />
              <KV k="Crema producida" v={`${p.cream_kg} kg`} />
              <KV k="Rendimiento crema" v={`${p.cream_yield_pct}%`} />
              <KV k="—" v="" />
              <KV k="Lote Cuajada" v={p.curd_folio} highlight />
              <KV k="Cuajada producida" v={`${p.curd_kg} kg`} />
              <KV k="Rendimiento cuajada" v={`${p.curd_yield_pct}%`} />
              <KV k="Notas" v={p.notes || "—"} />
            </Grid>
          </div>
        ))}
      </Section>

      {/* VENTAS Y COBRANZA */}
      <SalesSection data={data} />

      {/* RETORNABLES (solo si accedimos vía VTA-) */}
      {data.linked_via_sale && <ReturnablesSection saleFolio={data.linked_via_sale.folio} customerName={data.linked_via_sale.customer_name} />}

      {/* ANÁLISIS DE COMPRA (snapshot) */}
      {a && (
        <Section icon={Banknote} title="Análisis de compra (snapshot al registrar)" tone="navy" id="analisis-compra">
          <Grid cols={4}>
            <KV k="Decisión" v={a.decision} />
            <KV k="Margen" v={`${a.margin_pct}%`} />
            <KV k="Puntos de grasa" v={fmt(a.fat_points, 3)} />
            <KV k="Leche descremada" v={`${fmt(a.skim_milk, 3)} L`} />
            <KV k="Valor crema estimado" v={money(a.cream_value)} />
            <KV k="Cuajada estimada" v={`${fmt(a.curd_kg, 3)} kg`} />
            <KV k="Valor cuajada estimado" v={money(a.curd_value)} />
            <KV k="Ingreso total" v={money(a.total_income)} highlight />
            <KV k="Costo corrección estimado" v={money(a.correction_cost)} />
            <KV k="Utilidad bruta" v={money(a.gross_profit)} />
            <KV k="Utilidad ajustada" v={money(a.adjusted_profit)} highlight />
            <KV k="Lotes similares" v={a.similar_count} />
          </Grid>
          {a.conclusion && (
            <div className="mt-3 text-sm text-stone-700 bg-stone-100/60 rounded-md p-3 italic">"{a.conclusion}"</div>
          )}
          {a.alerts && a.alerts.length > 0 && (
            <div className="mt-3">
              <div className="text-[10px] uppercase tracking-wider font-outfit font-semibold text-stone-500 mb-1.5">Alertas en momento de compra</div>
              <ul className="space-y-1">
                {a.alerts.map((alert, i) => <li key={i} className="text-xs text-amber-900 flex gap-1.5"><span>⚠</span>{alert}</li>)}
              </ul>
            </div>
          )}
        </Section>
      )}
    </div>
  );
};

// ============================================================
// CREMA/CUAJADA DOSSIER (búsqueda por CREC-/CUAC-, compra a proveedor)
// ============================================================
const CreamCurdDossier = ({ data, onSelect }) => {
  const r = data.reception;
  const productLabel = data.product === "cream" ? "Crema" : "Cuajada";
  return (
    <div className="space-y-6" data-testid="trace-cream-curd-dossier">
      <Card className="p-6 border-stone-200 bg-gradient-to-br from-amber-50 to-white shadow-sm">
        {data.linked_via_sale && (
          <div className="mb-4 pb-4 border-b border-stone-200">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-[#1E3A8A] text-white text-[10px] uppercase tracking-wider font-outfit font-semibold">
                <ShoppingCart className="w-3 h-3" /> Trazabilidad vía venta
              </span>
              <span className="text-sm font-outfit font-semibold text-[#1E3A8A]">{data.linked_via_sale.folio}</span>
              <span className="text-stone-400">·</span>
              <span className="text-sm font-medium text-stone-800">{data.linked_via_sale.customer_name}</span>
              <span className="text-stone-400">·</span>
              <span className="text-sm tabular-nums text-stone-700">{money(data.linked_via_sale.total)}</span>
            </div>
          </div>
        )}
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-amber-600 text-white text-[10px] uppercase tracking-wider font-outfit font-semibold">
              <Droplets className="w-3 h-3" /> {productLabel} comprada a proveedor
            </span>
            <div className="text-3xl font-outfit font-bold text-stone-900 mt-1">{data.lote_origen}</div>
            <div className="text-sm text-stone-600 mt-2">{r.supplier_name} · Recibido el {r.purchase_date}</div>
          </div>
        </div>
      </Card>

      <Section icon={Building2} title="Proveedor" tone="stone" id="proveedor">
        <Grid>
          <KV k="Razón social" v={data.supplier?.name} />
          <KV k="Contacto" v={data.supplier?.contact} />
          <KV k="Teléfono" v={data.supplier?.phone} />
          <KV k="Notas" v={data.supplier?.notes} />
        </Grid>
      </Section>

      <Section icon={Droplets} title={`Recepción de ${productLabel.toLowerCase()} · ${r.folio}`} tone="navy" id="recepcion">
        <Grid cols={3}>
          <KV k="Fecha de recepción" v={r.purchase_date} />
          <KV k="Kg recibidos" v={`${fmt(r.kg, 2)} kg`} highlight />
          <KV k="Litros" v={r.liters ? `${fmt(r.liters, 2)} L` : "—"} />
          <KV k="Factura" v={r.invoice_number || "—"} />
          <KV k="Notas" v={r.notes || "—"} />
        </Grid>

        {!r.costs_hidden && (
          <div className="mt-4 pt-4 border-t border-stone-200">
            <div className="text-[10px] uppercase tracking-wider font-outfit font-semibold text-stone-500 mb-2">Costos de compra</div>
            <Grid cols={4}>
              <KV k="Costo total (a proveedor)" v={money(r.total_cost)} highlight />
              <KV k="Corrección acumulada" v={money(r.correction_cost_total)} />
              <KV k="Forma de pago" v={r.payment_method === "credito" ? `Crédito ${r.credit_days || 0}d` : "Contado"} />
            </Grid>
            {r.payment_method === "credito" && (
              <div className="mt-3 border border-emerald-200 bg-emerald-50 rounded-md p-3">
                <div className="text-[10px] uppercase tracking-wider font-outfit font-semibold text-emerald-700 mb-1">Compra ligada en Cuentas por Pagar</div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                  <div><span className="text-stone-500">Folio:</span> <Link to={`/cuentas-por-pagar`} className="font-mono text-[#1E3A8A] hover:underline">{r.folio}</Link></div>
                  <div><span className="text-stone-500">Total:</span> <span className="tabular-nums">{money(r.total_cost)}</span></div>
                  <div><span className="text-stone-500">Saldo:</span> <span className={`tabular-nums font-semibold ${(r.balance || 0) > 0 ? "text-amber-700" : "text-emerald-700"}`}>{money(r.balance)}</span></div>
                  <div><span className="text-stone-500">Vencimiento:</span> <span className="tabular-nums">{r.due_date || "—"}</span></div>
                </div>
              </div>
            )}
          </div>
        )}
      </Section>

      {data.product === "cream" && (
        <Section icon={Wrench} title={`Correcciones aplicadas (${data.corrections.length})`} tone="orange" id="correcciones">
          <p className="text-xs text-stone-500 mb-4 -mt-2">→ Bajan la acidez de este lote comprado. Su costo NO se suma a lo que se le debe al proveedor.</p>
          {data.corrections.length === 0 ? (
            <Empty>Sin correcciones aplicadas.</Empty>
          ) : data.corrections.map((c) => (
            <div key={c.id} className="border border-stone-200 rounded-lg p-4 mb-3 last:mb-0 bg-stone-50/40">
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs text-stone-500">{dt(c.created_at)} · {c.responsible}</div>
                <div className="text-sm font-outfit font-semibold text-[#1E3A8A] tabular-nums">{money(c.total_cost)}</div>
              </div>
              {c.items && c.items.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-white border-b border-stone-200">
                      <tr>{["Insumo", "Kg", "Precio $/kg", "Costo"].map((h) => <th key={h} className="px-3 py-2 text-left text-[10px] uppercase tracking-wider font-outfit font-semibold text-stone-500">{h}</th>)}</tr>
                    </thead>
                    <tbody>
                      {c.items.map((it, j) => (
                        <tr key={j} className="border-b border-stone-100 last:border-0">
                          <td className="px-3 py-2 font-medium text-stone-900">{it.name}</td>
                          <td className="px-3 py-2 tabular-nums">{it.kg} kg</td>
                          <td className="px-3 py-2 tabular-nums text-stone-700">${it.price_per_kg}</td>
                          <td className="px-3 py-2 tabular-nums font-medium">{money(it.cost)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {c.final_result && <div className="mt-3 text-xs text-stone-600"><b>Resultado:</b> {c.final_result}</div>}
            </div>
          ))}
        </Section>
      )}

      <SalesSection data={data} />
    </div>
  );
};

// ============================================================
// UI helpers
// ============================================================

const TONES = {
  stone:   { dot: "bg-stone-700", chip: "bg-stone-100 text-stone-700" },
  navy:    { dot: "bg-[#1E3A8A]", chip: "bg-[#1E3A8A]/10 text-[#1E3A8A]" },
  amber:   { dot: "bg-amber-600", chip: "bg-amber-100 text-amber-800" },
  orange:  { dot: "bg-orange-600", chip: "bg-orange-100 text-orange-800" },
  emerald: { dot: "bg-emerald-700", chip: "bg-emerald-100 text-emerald-800" },
};

const Section = ({ icon: Icon, title, tone = "stone", children, id }) => {
  const t = TONES[tone] || TONES.stone;
  return (
    <Card className="border-stone-200 bg-white shadow-sm overflow-hidden" data-print-section={id}>
      <div className="hidden print:flex print:items-center print:justify-between px-6 pt-6 pb-4 border-b-2 border-[#1E3A8A]">
        <PrintBrand />
        <div className="text-right">
          <div className="font-outfit font-bold text-sm uppercase tracking-wide text-[#1E3A8A]">Reporte de Trazabilidad</div>
          <div className="text-xs text-stone-500 mt-0.5">{new Date().toLocaleString("es-MX")}</div>
        </div>
      </div>
      <div className="px-6 py-4 border-b border-stone-200 flex items-center gap-3">
        <span className={`w-7 h-7 rounded-md ${t.chip} flex items-center justify-center flex-shrink-0`}>
          <Icon className="w-4 h-4" strokeWidth={1.75} />
        </span>
        <h2 className="font-outfit font-semibold text-stone-900">{title}</h2>
        {id && (
          <button
            type="button"
            onClick={() => printSection(id, `Trazabilidad - ${typeof title === "string" ? title : id} - LácteoPro`)}
            className="ml-auto print:hidden inline-flex items-center gap-1.5 text-xs text-stone-500 hover:text-[#1E3A8A] hover:bg-stone-100 transition-colors px-2 py-1 rounded"
            data-testid={`print-section-${id}`}
          >
            <Printer className="w-3.5 h-3.5" /> Imprimir/PDF
          </button>
        )}
      </div>
      <div className="p-6">{children}</div>
    </Card>
  );
};

const Grid = ({ cols = 2, children }) => (
  <div className={`grid grid-cols-2 ${cols >= 3 ? "md:grid-cols-3" : ""} ${cols >= 4 ? "lg:grid-cols-4" : ""} gap-x-6 gap-y-4`}>
    {children}
  </div>
);

const KV = ({ k, v, highlight }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wider font-outfit font-semibold text-stone-500">{k}</div>
    <div className={`mt-0.5 text-sm font-ibm-plex ${highlight ? "text-[#1E3A8A] font-semibold" : "text-stone-900"} break-words`}>
      {v === "" || v == null ? "—" : v}
    </div>
  </div>
);

const Stat = ({ label, value, accent }) => {
  const colors = {
    emerald: "text-emerald-700",
    amber: "text-amber-700",
    red: "text-red-700",
    default: "text-stone-900",
  };
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-outfit font-semibold text-stone-500">{label}</div>
      <div className={`mt-1 font-outfit font-bold text-xl tabular-nums ${colors[accent] || colors.default}`}>{value}</div>
    </div>
  );
};

const QualityPill = ({ label, info }) => {
  const map = {
    green: { bg: "bg-emerald-50 border-emerald-200", text: "text-emerald-800", Icon: CheckCircle2 },
    yellow: { bg: "bg-amber-50 border-amber-200", text: "text-amber-800", Icon: AlertTriangle },
    red: { bg: "bg-red-50 border-red-200", text: "text-red-800", Icon: XCircle },
    gray: { bg: "bg-stone-50 border-stone-200", text: "text-stone-700", Icon: CheckCircle2 },
  };
  const c = map[info.status] || map.gray;
  return (
    <div className={`rounded-md border ${c.bg} px-3 py-2`}>
      <div className="text-[10px] uppercase tracking-wider font-outfit font-semibold text-stone-600">{label}</div>
      <div className="flex items-center gap-1.5 mt-0.5">
        <c.Icon className={`w-3.5 h-3.5 ${c.text}`} />
        <span className={`text-sm font-medium ${c.text}`}>{info.value}</span>
      </div>
      <div className={`text-[11px] mt-0.5 ${c.text}`}>{info.label}</div>
    </div>
  );
};

const Empty = ({ children }) => (
  <p className="text-sm text-stone-500 italic">{children}</p>
);

const LabGroup = ({ title, description, samples, tone = "amber", showFull = true, id }) => (
  <Section icon={FlaskConical} title={title} tone={tone} id={id}>
    <p className="text-xs text-stone-500 mb-4 -mt-2">{description}</p>
    {samples.length === 0 ? (
      <Empty>Aún no hay muestras registradas para este lote.</Empty>
    ) : samples.map((s) => (
      <div key={s.id} className="border border-stone-200 rounded-lg p-4 mb-3 last:mb-0 bg-stone-50/40">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs text-stone-500">{dt(s.created_at)} · {s.responsible} · Lote {s.reception_folio}</div>
          {s.estado_cuajada && <span className="text-[10px] uppercase tracking-wider font-outfit font-semibold px-2 py-0.5 rounded bg-amber-100 text-amber-800">{s.estado_cuajada}</span>}
        </div>
        <Grid cols={4}>
          <KV k="pH" v={s.ph ?? "—"} />
          <KV k="Acidez" v={s.acidity != null ? `${s.acidity} °D` : "—"} />
          <KV k="Temperatura" v={s.temperature_c != null ? `${s.temperature_c} °C` : "—"} />
          {showFull ? <KV k="Grasa butírica" v={s.fat_pct != null ? `${s.fat_pct}%` : "—"} /> : <KV k="Humedad" v={s.humidity_pct != null ? `${s.humidity_pct}%` : "—"} />}
          {showFull && <>
            <KV k="Proteína" v={s.protein_pct != null ? `${s.protein_pct}%` : "—"} />
            <KV k="Lactosa" v={s.lactose_pct != null ? `${s.lactose_pct}%` : "—"} />
            <KV k="Densidad" v={s.density ?? "—"} />
            <KV k="Crioscópico" v={s.crioscopic_h != null ? `${s.crioscopic_h} °H` : "—"} />
            <KV k="SNG" v={s.sng_pct != null ? `${s.sng_pct}%` : "—"} />
            <KV k="Sólidos totales" v={s.total_solids_pct != null ? `${s.total_solids_pct}%` : "—"} />
            <KV k="Alcohol 75%" v={s.alcohol_test || "—"} />
            <KV k="Neutralizantes" v={s.neutralizers || "—"} />
            <KV k="Agente clorado" v={s.chlorinates || "—"} />
            <KV k="Antibiótico" v={s.antibiotic || "—"} />
            <KV k="Color" v={s.color || "—"} />
            <KV k="Olor" v={s.smell || "—"} />
            <KV k="Sabor" v={s.taste || "—"} />
            <KV k="Aspecto" v={s.aspect || "—"} />
          </>}
        </Grid>
        {s.notes && <div className="mt-3 text-xs text-stone-600"><b>Notas:</b> {s.notes}</div>}
        {s.photo_url && (
          <div className="mt-3">
            <div className="text-[10px] uppercase tracking-wider font-outfit font-semibold text-stone-500 mb-1">Evidencia</div>
            <a href={s.photo_url} target="_blank" rel="noreferrer" className="text-xs text-[#1E3A8A] hover:underline break-all">{s.photo_url}</a>
          </div>
        )}
      </div>
    ))}
  </Section>
);


// ============================================================
// SALES + COBRANZA section (linked to this lote)
// ============================================================

const PAY_METHOD_LABEL = { efectivo: "Efectivo", transferencia: "Transferencia", cheque: "Cheque", otro: "Otro" };

const SalesSection = ({ data }) => {
  const sales = data?.sales || [];
  const sum = data?.sales_summary || { sold_count: 0, sold_amount: 0, collected: 0, pending: 0 };
  const today = new Date();
  return (
    <Section icon={ShoppingCart} title={`Ventas y cobranza (${sum.sold_count})`} tone="navy" id="ventas-cobranza">
      {sales.length === 0 ? (
        <Empty>Este lote aún no se ha vendido. Registra una venta en el módulo Ventas.</Empty>
      ) : (
        <>
          {/* Mini KPIs proporcionales al lote */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <MiniKPI icon={ShoppingCart} label="Ventas del lote" value={sum.sold_count} tone="navy" />
            <MiniKPI icon={Receipt} label="Facturado" value={money(sum.sold_amount)} tone="navy" />
            <MiniKPI icon={Wallet} label="Cobrado" value={money(sum.collected)} tone="emerald" />
            <MiniKPI icon={CreditCard} label="Pendiente" value={money(sum.pending)} tone={sum.pending > 0 ? "amber" : "stone"} />
          </div>

          <div className="space-y-3">
            {sales.map((s) => {
              const overdue = s.sale_balance > 0 && s.due_date && new Date(s.due_date) < today;
              const paymentBadge = s.payment_method === "credito"
                ? <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-outfit font-semibold ${overdue ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"}`}><CreditCard className="w-3 h-3" /> Crédito {s.credit_days}d{overdue && " (VENCIDA)"}</span>
                : <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 text-[10px] uppercase tracking-wider font-outfit font-semibold"><Wallet className="w-3 h-3" /> Contado</span>;
              return (
                <div key={s.sale_id} className={`border rounded-lg p-4 ${overdue ? "border-red-200 bg-red-50/30" : "border-stone-200 bg-white"}`} data-testid={`traza-sale-${s.folio}`}>
                  <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-outfit font-semibold text-[#1E3A8A]">{s.folio}</span>
                        <span className="text-stone-400">·</span>
                        <span className="text-sm font-medium text-stone-800">{s.customer_name}</span>
                        {paymentBadge}
                      </div>
                      <div className="text-xs text-stone-500 mt-1">
                        Fecha venta {s.sale_date} · {s.due_date ? `vence ${s.due_date}` : "sin vencimiento"}
                        {s.shipment_folio && <> · embarque <Link to={`/trazabilidad?lote=${s.shipment_folio}`} className="font-mono text-[#1E3A8A] hover:underline">{s.shipment_folio}</Link></>}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-outfit font-semibold tabular-nums text-stone-900">{money(s.lote_amount)}</div>
                      <div className="text-[10px] uppercase tracking-wider text-stone-500 font-outfit">Facturado de este lote</div>
                    </div>
                  </div>

                  <Grid cols={4}>
                    <KV k="Cobrado (proporcional)" v={money(s.lote_collected)} highlight={s.lote_collected > 0} />
                    <KV k="Pendiente (proporcional)" v={money(s.lote_pending)} />
                    <KV k="Total factura" v={money(s.sale_total)} />
                    <KV k="Saldo factura" v={money(s.sale_balance)} />
                  </Grid>

                  {/* Items del lote vendidos */}
                  <div className="mt-3">
                    <div className="text-[10px] uppercase tracking-wider font-outfit font-semibold text-stone-500 mb-1.5">Items vendidos de este lote</div>
                    <div className="flex flex-wrap gap-1.5">
                      {s.matched_items.map((it, i) => (
                        <span key={i} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-stone-100 border border-stone-200 text-stone-700">
                          <span className="font-outfit font-semibold text-stone-900">{it.product}</span>
                          <span className="text-stone-400">·</span>
                          <span className="font-mono text-[#1E3A8A]">{it.lote}</span>
                          <span className="text-stone-400">·</span>
                          <span className="tabular-nums">{it.quantity} {it.unit} × {money(it.unit_price)}</span>
                          <span className="text-stone-400">=</span>
                          <span className="tabular-nums font-medium text-stone-800">{money(it.subtotal)}</span>
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Embarque asociado */}
                  {s.shipment && (
                    <div className="mt-3 border border-blue-200 bg-blue-50/50 rounded-md p-3">
                      <div className="flex items-center gap-2 mb-1.5">
                        <Truck className="w-4 h-4 text-[#1E3A8A]" />
                        <span className="text-[10px] uppercase tracking-wider font-outfit font-semibold text-[#1E3A8A]">Embarque asociado</span>
                        <Link to={`/trazabilidad?lote=${s.shipment.folio}`} className="text-xs font-outfit font-semibold text-[#1E3A8A] hover:underline ml-auto">{s.shipment.folio} →</Link>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                        <div><span className="text-stone-500">Estado:</span> <span className="font-medium text-stone-800 capitalize">{(s.shipment.status || "").replace("_", " ")}</span></div>
                        <div><span className="text-stone-500">Chofer:</span> <span className="text-stone-800">{s.shipment.driver_name || "—"}</span></div>
                        <div><span className="text-stone-500">Placa:</span> <span className="font-mono text-stone-800">{s.shipment.vehicle_plate || "—"}</span></div>
                        <div><span className="text-stone-500">Temp. out/in:</span> <span className="tabular-nums text-stone-800">{s.shipment.temperature_out_c ?? "—"}° / {s.shipment.temperature_in_c ?? "—"}°</span></div>
                        {(s.shipment.freight_cost || 0) > 0 && (
                          <>
                            <div><span className="text-stone-500">Flete:</span> <span className="tabular-nums font-semibold text-amber-700">{money(s.shipment.freight_cost)}</span></div>
                            <div className="col-span-2"><span className="text-stone-500">Transportista:</span> <span className="text-stone-800">{s.shipment.freight_supplier_name || "—"}</span></div>
                            {s.shipment.freight_purchase_id && <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-outfit">→ CxP generada</div>}
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Pagos aplicados */}
                  {s.payments.length > 0 && (
                    <div className="mt-3">
                      <div className="text-[10px] uppercase tracking-wider font-outfit font-semibold text-stone-500 mb-1.5">Pagos aplicados ({s.payments.length})</div>
                      <div className="space-y-1">
                        {s.payments.map((p) => (
                          <div key={p.id} className="flex items-center gap-2 text-xs text-stone-700 py-1 px-2 rounded bg-emerald-50 border border-emerald-100">
                            <span className="tabular-nums font-mono text-stone-500 w-24">{p.payment_date}</span>
                            <span className="px-1.5 py-0.5 rounded bg-white border border-stone-200 text-[10px] uppercase tracking-wider font-outfit font-semibold text-stone-600">{PAY_METHOD_LABEL[p.method] || p.method}</span>
                            <span className="tabular-nums font-semibold text-emerald-700 ml-auto">{money(p.amount)}</span>
                            {p.reference && <span className="text-[10px] text-stone-500 font-mono">{p.reference}</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </Section>
  );
};

const MiniKPI = ({ icon: Icon, label, value, tone = "stone" }) => {
  const t = TONES[tone] || TONES.stone;
  return (
    <div className={`border border-stone-200 rounded-md p-3 bg-white`}>
      <div className="flex items-center gap-1.5 text-stone-500">
        <span className={`w-1.5 h-1.5 rounded-full ${t.dot}`} />
        <span className="text-[10px] font-outfit uppercase tracking-wider">{label}</span>
      </div>
      <div className="mt-1.5 flex items-center gap-2">
        <Icon className="w-3.5 h-3.5 text-stone-400" strokeWidth={1.75} />
        <span className="font-outfit font-semibold text-lg text-stone-900 tabular-nums">{value}</span>
      </div>
    </div>
  );
};

// ============================================================
// RETORNABLES section (linked to a sale)
// ============================================================
import { Label as TrLabel } from "@/components/ui/label";
import { Dialog as TrDialog, DialogContent as TrDialogContent, DialogHeader as TrDialogHeader, DialogTitle as TrDialogTitle, DialogDescription as TrDialogDescription } from "@/components/ui/dialog";

const ReturnablesSection = ({ saleFolio, customerName }) => {
  const [data, setData] = useState(null);
  const [returnFor, setReturnFor] = useState(null);
  const [qty, setQty] = useState("");

  const load = () => api.get(`/returnables/by-sale/${saleFolio}`).then((r) => setData(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [saleFolio]);

  const submitReturn = async (e) => {
    e.preventDefault();
    const q = parseInt(qty);
    if (!q || q <= 0) return toast.error("Cantidad inválida");
    if (q > returnFor.outstanding) return toast.error(`Máximo ${returnFor.outstanding} pendientes`);
    try {
      await api.post("/returnables/movements", { type_code: returnFor.type_code, direction: "in", qty: q, customer_name: customerName, related_sale_folio: saleFolio, notes: `Regreso desde ficha VTA ${saleFolio}` });
      toast.success(`${q} × ${returnFor.type_name} recibidos`);
      setReturnFor(null);
      setQty("");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  if (!data || (data.totals?.delivered || 0) === 0) return null;
  const summary = data.summary_by_type || [];

  return (
    <Section icon={RotateCcw} title={`Retornables (${data.totals.outstanding} pendientes de regresar)`} tone="navy" id="retornables">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
        <div className="p-3 rounded-md bg-stone-50 border border-stone-200 text-center">
          <div className="text-2xl font-outfit font-bold tabular-nums text-stone-900">{data.totals.delivered}</div>
          <div className="text-[10px] uppercase tracking-wider font-outfit text-stone-500">Entregados</div>
        </div>
        <div className="p-3 rounded-md bg-emerald-50 border border-emerald-200 text-center">
          <div className="text-2xl font-outfit font-bold tabular-nums text-emerald-700">{data.totals.returned}</div>
          <div className="text-[10px] uppercase tracking-wider font-outfit text-emerald-700">Regresados</div>
        </div>
        <div className="p-3 rounded-md bg-amber-50 border border-amber-200 text-center">
          <div className="text-2xl font-outfit font-bold tabular-nums text-amber-700">{data.totals.outstanding}</div>
          <div className="text-[10px] uppercase tracking-wider font-outfit text-amber-700">Pendientes</div>
        </div>
      </div>
      <div className="space-y-2">
        {summary.map((s) => (
          <div key={s.type_code} className="flex items-center justify-between p-3 rounded-md border border-stone-200 bg-white">
            <div className="flex items-center gap-2">
              <Package className="w-4 h-4 text-stone-500" />
              <div>
                <div className="font-medium text-stone-900">{s.type_name}</div>
                <div className="text-xs text-stone-500">Entregados {s.delivered} · Regresados {s.returned}</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right">
                <div className={`font-outfit font-bold text-lg tabular-nums ${s.outstanding > 0 ? "text-amber-700" : "text-emerald-700"}`}>{s.outstanding}</div>
                <div className="text-[10px] uppercase tracking-wider font-outfit text-stone-500">Pendientes</div>
              </div>
              {s.outstanding > 0 && (
                <Button size="sm" onClick={() => { setReturnFor(s); setQty(s.outstanding.toString()); }} data-testid={`ret-return-${s.type_code}`} className="bg-emerald-600 hover:bg-emerald-700 text-white print:hidden">
                  <ArrowDownLeft className="w-3.5 h-3.5 mr-1" /> Registrar regreso
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      <TrDialog open={!!returnFor} onOpenChange={(o) => !o && setReturnFor(null)}>
        <TrDialogContent className="max-w-sm">
          <TrDialogHeader>
            <TrDialogTitle className="font-outfit">Registrar regreso · {returnFor?.type_name}</TrDialogTitle>
            <TrDialogDescription>{customerName} · máximo {returnFor?.outstanding} pendientes</TrDialogDescription>
          </TrDialogHeader>
          <form onSubmit={submitReturn} className="space-y-3">
            <div>
              <TrLabel className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600">Cantidad *</TrLabel>
              <Input type="number" min="1" max={returnFor?.outstanding} value={qty} onChange={(e) => setQty(e.target.value)} className="mt-1" autoFocus data-testid="ret-return-qty" />
            </div>
            <div className="flex gap-2 justify-end pt-2 border-t">
              <Button type="button" variant="outline" onClick={() => setReturnFor(null)}>Cancelar</Button>
              <Button type="submit" data-testid="ret-return-submit" className="bg-emerald-600 hover:bg-emerald-700 text-white">Confirmar</Button>
            </div>
          </form>
        </TrDialogContent>
      </TrDialog>
    </Section>
  );
};



// ============================================================
// SHIPMENT DOSSIER (búsqueda por EMB-)
// ============================================================
const ShipmentDossier = ({ data }) => {
  const s = data.shipment || {};
  const sales = data.sales || [];
  const fp = data.freight_purchase;
  const involvedLots = data.involved_lots || [];
  return (
    <div className="space-y-6" data-testid="trace-shipment-dossier">
      <Card className="p-6 border-stone-200 dark:border-stone-800 bg-gradient-to-br from-blue-50 to-white dark:from-blue-950/20 dark:to-stone-950 shadow-sm">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-[#1E3A8A] text-white text-[10px] uppercase tracking-wider font-outfit font-semibold">
                <Truck className="w-3 h-3" /> Embarque
              </span>
              <span className="text-2xl font-outfit font-bold text-[#1E3A8A]">{s.folio}</span>
              <span className="text-xs uppercase tracking-wider font-outfit font-semibold px-2 py-0.5 rounded bg-stone-100 text-stone-700 capitalize">{(s.status || "").replace("_", " ")}</span>
            </div>
            <div className="text-sm text-stone-600">{s.customer_name} · {s.shipment_date}</div>
          </div>
          <div className="text-right text-xs text-stone-500">
            Creado por {s.created_by || "—"}<br/>
            {s.created_at ? dt(s.created_at) : ""}
          </div>
        </div>
      </Card>

      <Section icon={Truck} title="Datos de transporte" tone="navy" id="embarque-transporte">
        <Grid cols={4}>
          <KV k="Chofer" v={s.driver_name || "—"} />
          <KV k="Placa" v={<span className="font-mono">{s.vehicle_plate || "—"}</span>} />
          <KV k="Origen" v={s.origin || "—"} />
          <KV k="Destino" v={s.destination || "—"} />
          <KV k="Hora salida" v={s.time_out || "—"} />
          <KV k="Hora llegada" v={s.time_in || "—"} />
          <KV k="Temp. salida" v={s.temperature_out_c != null ? `${s.temperature_out_c}°C` : "—"} />
          <KV k="Temp. llegada" v={s.temperature_in_c != null ? `${s.temperature_in_c}°C` : "—"} />
        </Grid>
        {s.notes && <div className="mt-3 text-xs text-stone-600"><span className="text-stone-500 uppercase tracking-wider font-outfit font-semibold">Notas: </span>{s.notes}</div>}
      </Section>

      {(s.freight_cost || 0) > 0 && (
        <Section icon={ShoppingCart} title="Flete contratado" tone="navy" id="embarque-flete">
          <Grid cols={4}>
            <KV k="Transportista" v={s.freight_supplier_name || "—"} />
            <KV k="Costo del flete" v={<span className="font-semibold text-amber-700">{money(s.freight_cost)}</span>} highlight />
            <KV k="Forma de pago" v={s.freight_payment_method === "credito" ? `Crédito ${s.freight_credit_days || 0}d` : "Contado"} />
            <KV k="Factura" v={s.freight_invoice_number || "—"} />
          </Grid>
          {fp && (
            <div className="mt-3 border border-emerald-200 bg-emerald-50 rounded-md p-3">
              <div className="text-[10px] uppercase tracking-wider font-outfit font-semibold text-emerald-700 mb-1">Compra ligada en Cuentas por Pagar</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                <div><span className="text-stone-500">Folio:</span> <Link to={`/cuentas-por-pagar`} className="font-mono text-[#1E3A8A] hover:underline">{fp.folio}</Link></div>
                <div><span className="text-stone-500">Total:</span> <span className="tabular-nums">{money(fp.amount)}</span></div>
                <div><span className="text-stone-500">Saldo:</span> <span className="tabular-nums font-semibold text-amber-700">{money(fp.balance)}</span></div>
                <div><span className="text-stone-500">Vencimiento:</span> <span className="tabular-nums">{fp.due_date || "—"}</span></div>
              </div>
            </div>
          )}
        </Section>
      )}

      <Section icon={ShoppingCart} title={`Ventas del embarque (${sales.length})`} tone="navy" id="embarque-ventas">
        {sales.length === 0 ? (
          <Empty>Este embarque no tiene ventas vinculadas.</Empty>
        ) : (
          <div className="space-y-2">
            {sales.map((sale) => (
              <div key={sale.id} className="border border-stone-200 rounded-md p-3 flex items-center gap-3 flex-wrap bg-white" data-testid={`ship-linked-sale-${sale.folio}`}>
                <Link to={`/trazabilidad?lote=${sale.folio}`} className="text-sm font-outfit font-semibold text-[#1E3A8A] hover:underline">{sale.folio}</Link>
                <span className="text-stone-400">·</span>
                <span className="text-sm text-stone-700">{sale.customer_name}</span>
                <span className="text-stone-400">·</span>
                <span className="text-xs text-stone-500">{sale.sale_date}</span>
                <span className="ml-auto tabular-nums font-medium text-stone-800">{money(sale.total)}</span>
                {(sale.balance || 0) > 0 && <span className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-800 font-outfit font-semibold uppercase tracking-wider">Saldo {money(sale.balance)}</span>}
              </div>
            ))}
          </div>
        )}
      </Section>

      {involvedLots.length > 0 && (
        <Section icon={Package} title={`Lotes de origen involucrados (${involvedLots.length})`} tone="navy" id="embarque-lotes">
          <div className="flex flex-wrap gap-1.5">
            {involvedLots.map((f) => (
              <Link key={f} to={`/trazabilidad?lote=${f}`} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-white border border-stone-300 font-mono text-[#1E3A8A] hover:bg-[#1E3A8A]/5">
                {f}
              </Link>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
};
